import { useState, useRef, useEffect, useCallback } from 'react';
import '../App.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import FrameList from './CanvasFloatingFrameList';
import FrameStickSpecPanel from './FrameStickSpecPanel';
import { totalPrice as calculatePrice } from './PricingEngine';
import {
  CUSTOM_SIZE_OPTION,
  customSizeOptions,
  parseCustomDims,
  getImageSizeInches,
  getPrintAreaInches,
  getEffectiveOpeningCm,
  nearestTierForPricing as computeNearestPrintTier,
  computePreviewCanvasSize,
  getCustomSizeMetaString
} from './customPrintSize';

const STRETCH_TIERS = [
  { id: 'small', label: 'Small (up to 32" x 40")', price: 7.95 },
  { id: 'medium', label: 'Medium (up to 40" x 60")', price: 9.45 },
  { id: 'large', label: 'Large (up to 48" x 96")', price: 10.75 }
];

const FLOAT_FRAME_GAP_DISCLAIMER_PREFIX = 'Disclaimer : Standard float frame gap =';

const minStretchTierForDims = (wIn, hIn) => {
  const L = Math.max(wIn, hIn);
  const S = Math.min(wIn, hIn);
  if (L <= 40 && S <= 32) return 'small';
  if (L <= 60 && S <= 40) return 'medium';
  return 'large';
};

function CanvasPrintAndFraming({ image, onEditImage }) {
  const userImage = image || localStorage.getItem('opticon_last_image');
  const canvasRef = useRef(null);
  const [printType, setPrintType] = useState('canvas');
  const [sideType, setSideType] = useState('mirror');
  const [frameOpen, setFrameOpen] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [imageSizeCm, setImageSizeCm] = useState(null);
  const [customSize, setCustomSize] = useState(CUSTOM_SIZE_OPTION);
  const [customWidthCm, setCustomWidthCm] = useState('');
  const [customHeightCm, setCustomHeightCm] = useState('');
  const [appliedCustomCropKey, setAppliedCustomCropKey] = useState('');
  const [floatFrameGapMm, setFloatFrameGapMm] = useState('5');
  const DPI = 300;
  const CM_TO_MM = 10;

  const printOptions = [
    { id: 'lustre', label: 'Lustre 300 GSM' },
    { id: 'light-art', label: 'Light Art Paper (210 GSM)' },
    { id: 'canvas', label: 'Canvas (Satin)' },
    { id: 'entrada', label: 'Entrada Cotton Natural Rag 290 GSM' }
  ];

  const galleryOptions = [
    { id: 'mirror', label: 'Image Mirrored' },
    { id: 'black', label: 'Printed Black' },
    { id: 'white', label: 'Printed White' }
  ];
  const { widthIn, heightIn, normalizedSize } = getImageSizeInches(imageSizeCm);

  const cmToInches = (cm) => {
    const parsed = parseFloat(cm);
    if (!parsed || parsed <= 0) return 0;
    return parsed / 2.54;
  };

  const customDimsParsed = () => {
    const widthInches = cmToInches(customWidthCm);
    const heightInches = cmToInches(customHeightCm);
    return parseCustomDims(widthInches, heightInches);
  };

  const isCustomWhMode = customSize === CUSTOM_SIZE_OPTION;
  const customDims = isCustomWhMode ? customDimsParsed() : null;
  const customWidthValue = Number(customWidthCm);
  const customHeightValue = Number(customHeightCm);
  const hasValidCustomSize =
    Number.isFinite(customWidthValue) &&
    Number.isFinite(customHeightValue) &&
    customWidthValue > 0 &&
    customHeightValue > 0;
  const customCropKey = hasValidCustomSize
    ? `${customWidthValue}x${customHeightValue}`
    : '';
  const showApplyCustomCrop =
    isCustomWhMode && hasValidCustomSize && customCropKey !== appliedCustomCropKey;

  const handleApplyCustomCrop = () => {
    if (!showApplyCustomCrop || !onEditImage) return;

    setAppliedCustomCropKey(customCropKey);
    onEditImage({
      aspect: customWidthValue / customHeightValue,
      customWidthCm: customWidthValue,
      customHeightCm: customHeightValue
    });
  };

  const area = getPrintAreaInches(customSize, customDims, widthIn, heightIn);
  const { previewCanvasW, previewCanvasH } = computePreviewCanvasSize(area);
  const effOpeningCm = getEffectiveOpeningCm(customSize, customDims, imageSizeCm);

  const nearestTier = computeNearestPrintTier({
    printType,
    customSize,
    customDims,
    widthIn,
    heightIn
  });

  const stretchType = minStretchTierForDims(area.w, area.h);
  const selectedStretchTier = STRETCH_TIERS.find((tier) => tier.id === stretchType);
  const printPrice = Number(nearestTier.price) || 0;
  const stretchPrice = Number(selectedStretchTier?.price) || 0;
  const floatFrameGapValue = Number(floatFrameGapMm);
  const hasValidFloatFrameGap =
    Number.isFinite(floatFrameGapValue) && floatFrameGapValue >= 0;

  const calculateFramePrice = () => {
    if (!imageSizeCm || !selectedFrame) return 0;

    const width =
      Number(effOpeningCm.widthCm) > 0
        ? effOpeningCm.widthCm
        : Number(imageSizeCm.widthCm);
    const height =
      Number(effOpeningCm.heightCm) > 0
        ? effOpeningCm.heightCm
        : Number(imageSizeCm.heightCm);

    const pricingResult = calculatePrice({
      qty: 1,
      opening_width: width * CM_TO_MM,
      opening_height: height * CM_TO_MM,
      frame: selectedFrame || {},
      mat: {},
      glass: {},
      backing: {},
      extras: [],
      openings: 1,
      discount: 0
    });

    return pricingResult.finalPrice;
  };

  const framePrice = calculateFramePrice();
  const totalPrice = printPrice + stretchPrice + framePrice;
  const gstAmount = totalPrice * 0.10;
  const totalWithGST = totalPrice + gstAmount;

  const getPrintFilter = useCallback(() => {
    switch (printType) {
      case 'lustre':
        return 'contrast(1.08) saturate(1.1)';
      case 'light-art':
        return 'contrast(1.02) saturate(0.95)';
      case 'canvas':
        return 'contrast(1.05) saturate(1.08)';
      case 'entrada':
        return 'contrast(0.98) saturate(0.9) brightness(1.02)';
      default:
        return 'none';
    }
  }, [printType]);

  const renderCanvas = useCallback(async () => {
    if (!userImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = userImage;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const W = canvas.width;
    const H = canvas.height;

    const frameThickness = selectedFrame
      ? Number(selectedFrame.thickness) || 0
      : 0;

    const innerX = frameThickness;
    const innerY = frameThickness;
    const innerW = W - frameThickness * 2;
    const innerH = H - frameThickness * 2;

    /* ========================================================
       CLEAR CANVAS
    ======================================================== */

    ctx.clearRect(0, 0, W, H);


    /* ========================================================
       DRAW PHOTO
    ======================================================== */

    ctx.filter = getPrintFilter();

    ctx.drawImage(
      img,
      innerX,
      innerY,
      innerW,
      innerH
    );

    ctx.filter = 'none';


    /* ========================================================
       DRAW FRAME
    ======================================================== */

    if (selectedFrame && frameThickness > 0) {

      const frameImg = new Image();
      frameImg.crossOrigin = 'anonymous';
      frameImg.src = selectedFrame.img;

      await new Promise((resolve, reject) => {
        frameImg.onload = resolve;
        frameImg.onerror = reject;
      });


      /*
        Miter size.
  
        This controls the 45° cut where
        the frame mouldings meet.
      */
      const miter = frameThickness;


      /* ======================================================
         TOP FRAME
         Original frame moulding direction
      ====================================================== */

      ctx.save();

      ctx.beginPath();

      // Outer top-left
      ctx.moveTo(0, 0);

      // Outer top-right
      ctx.lineTo(W, 0);

      // Inner top-right 45° miter
      ctx.lineTo(W - miter, frameThickness);

      // Inner top-left 45° miter
      ctx.lineTo(miter, frameThickness);

      ctx.closePath();

      ctx.clip();

      ctx.drawImage(
        frameImg,
        0,
        0,
        W,
        frameThickness
      );

      ctx.restore();


      /* ======================================================
         BOTTOM FRAME
         180° OPPOSITE OF TOP
  
         This is the important fix.
  
         The texture is rotated 180° so the moulding/profile
         faces the image in the opposite direction.
      ====================================================== */

      ctx.save();

      ctx.beginPath();

      // Inner bottom-left 45° miter
      ctx.moveTo(miter, H - frameThickness);

      // Inner bottom-right 45° miter
      ctx.lineTo(W - miter, H - frameThickness);

      // Outer bottom-right
      ctx.lineTo(W, H);

      // Outer bottom-left
      ctx.lineTo(0, H);

      ctx.closePath();

      ctx.clip();

      /*
        Flip the TOP moulding by 180°.
  
        This makes the bottom profile face
        toward the image/opening instead of
        facing outward.
      */
      ctx.translate(W, H);
      ctx.rotate(Math.PI);

      ctx.drawImage(
        frameImg,
        0,
        0,
        W,
        frameThickness
      );

      ctx.restore();


      /* ======================================================
         LEFT FRAME
         Vertical moulding
         Top and bottom corners are mitered
      ====================================================== */

      ctx.save();

      ctx.beginPath();

      // Outer top-left
      ctx.moveTo(0, 0);

      // Inner top-left 45° miter
      ctx.lineTo(frameThickness, miter);

      // Inner bottom-left 45° miter
      ctx.lineTo(frameThickness, H - miter);

      // Outer bottom-left
      ctx.lineTo(0, H);

      ctx.closePath();

      ctx.clip();

      /*
        Rotate the same frame texture 90° counter-clockwise.
        The moulding runs vertically.
      */
      ctx.translate(0, H);
      ctx.rotate(-Math.PI / 2);

      ctx.drawImage(
        frameImg,
        0,
        0,
        H,
        frameThickness
      );

      ctx.restore();


      /* ======================================================
         RIGHT FRAME
         Vertical moulding
         Top and bottom corners are mitered
      ====================================================== */

      ctx.save();

      ctx.beginPath();

      // Inner top-right 45° miter
      ctx.moveTo(W - frameThickness, miter);

      // Outer top-right
      ctx.lineTo(W, 0);

      // Outer bottom-right
      ctx.lineTo(W, H);

      // Inner bottom-right 45° miter
      ctx.lineTo(W - frameThickness, H - miter);

      ctx.closePath();

      ctx.clip();

      /*
        Rotate the same frame texture 90° clockwise.
        The moulding runs vertically.
      */
      ctx.translate(W, 0);
      ctx.rotate(Math.PI / 2);

      ctx.drawImage(
        frameImg,
        0,
        0,
        H,
        frameThickness
      );

      ctx.restore();
    }

  }, [getPrintFilter, selectedFrame, userImage]);

  useEffect(() => {
    renderCanvas();
  }, [renderCanvas, previewCanvasW, previewCanvasH]);

  useEffect(() => {
    if (!userImage) return;

    const img = new Image();
    img.onload = () => {
      const widthCm = ((img.width / DPI) * 2.54).toFixed(1);
      const heightCm = ((img.height / DPI) * 2.54).toFixed(1);

      setImageSizeCm({
        widthCm,
        heightCm,
        widthPx: img.width,
        heightPx: img.height
      });
    };

    img.src = userImage;
  }, [userImage]);

  useEffect(() => {
    if (!selectedFrame && FrameList.length > 0) {
      setSelectedFrame(FrameList[0]);
    }
  }, [selectedFrame]);

  const exportEditedImage = () => canvasRef.current.toDataURL('image/png');

  const handleAddToCart = async () => {
    if (!canvasRef.current) {
      alert('Image not ready.');
      return;
    }

    if (!imageSizeCm) {
      alert('Image size is still loading. Please try again.');
      return;
    }

    if (isCustomWhMode && !hasValidCustomSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Print Size Required',
        text: 'Please enter the print size width and height in cm.'
      });
      return;
    }

    if (!hasValidFloatFrameGap) {
      Swal.fire({
        icon: 'warning',
        title: 'Float Frame Gap Required',
        text: 'Please enter a valid float frame gap in mm.'
      });
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      /* ================================
         SEND THE ORIGINAL USER IMAGE
         (cropped/uploaded), not the
         rendered canvas with frame/mat
      ================================ */
      let editedImageBase64 = userImage;

      if (!editedImageBase64.startsWith('data:image')) {
        const resImg = await fetch(editedImageBase64);
        const blob = await resImg.blob();

        editedImageBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }

      const customSizeMeta = getCustomSizeMetaString(
        normalizedSize,
        customSize,
        customDimsParsed()
      );

      const payload = {
        id: 9855,
        quantity: 1,
        meta_data: [
          { key: 'print_type', value: printType },
          {
            key: 'image_size',
            value: `${imageSizeCm.widthCm} x ${imageSizeCm.heightCm} cm`
          },
          { key: 'custom_size', value: customSizeMeta },
          { key: 'print_size', value: nearestTier.key },
          { key: 'print_nearest_tier', value: nearestTier.key },
          { key: 'print_price', value: printPrice },
          { key: 'side_type', value: sideType },
          { key: 'stretch_value', value: stretchType },
          { key: 'stretch_size_tier', value: stretchType },
          { key: 'stretch_price', value: stretchPrice },
          { key: 'frame_name', value: selectedFrame?.name || 'No Frame' },
          { key: 'float_frame_gap_mm', value: floatFrameGapValue },
          { key: 'float_frame_gap', value: `${floatFrameGapValue}mm` },
          {
            key: 'float_frame_gap_disclaimer',
            value: `${FLOAT_FRAME_GAP_DISCLAIMER_PREFIX} ${floatFrameGapValue}mm`
          },
          { key: 'frame_price', value: framePrice },
          { key: 'total_price', value: totalWithGST },
          { key: 'edited_image', value: editedImageBase64 }
        ]
      };

      console.log('Canvas Print & Framing API Payload:', payload);

      const response = await axios.post(
        'https://opticonframing.com.au/wp-json/opticon/v1/canvas-print-and-framing/add-to-cart',
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const percent = Math.round((e.loaded * 100) / e.total);
            setProgress(percent);
          }
        }
      );

      if (response.data?.success) {
        setProgress(100);

        setTimeout(() => {
          window.location.href = 'https://opticonframing.com.au/cart';
        }, 700);
      } else {
        throw new Error(response.data?.error || 'Server error');
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      alert('Failed to add to cart. Check console for details.');
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <div className="product-page">
      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF',
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            {progress}%
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          width={previewCanvasW}
          height={previewCanvasH}
          className="real-canvas-preview"
        />
      </div>

      <div className="product-info-panel">
        <h1 className="product-title">Canvas Print & Framing</h1>

        <div className="print-option-row">
          <label className="form-label">Custom Size</label>

          <select
            className="form-select"
            value={customSize}
            onChange={(e) => setCustomSize(e.target.value)}
          >
            <option value={CUSTOM_SIZE_OPTION}>
              Custom width x height (cm)
            </option>

            <option value="">
              Auto (Based on image size)
            </option>

            {customSizeOptions.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {isCustomWhMode && (
          <div className="print-size-canvas-block">
            <label className="form-label print-size-canvas-block__title">
              Print Size
            </label>

            <div className="print-size-canvas-block__row">
              <div className="print-size-canvas-block__field">
                <span className="print-size-canvas-block__dim-label" id="print-canvas-w-label">
                  W
                </span>

                <input
                  id="print-canvas-w-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-w-label"
                  value={customWidthCm}
                  onChange={(e) => setCustomWidthCm(e.target.value)}
                />
              </div>

              <div className="print-size-canvas-block__field">
                <span className="print-size-canvas-block__dim-label" id="print-canvas-h-label">
                  H
                </span>

                <input
                  id="print-canvas-h-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-h-label"
                  value={customHeightCm}
                  onChange={(e) => setCustomHeightCm(e.target.value)}
                />
              </div>
            </div>

            {showApplyCustomCrop && (
              <button
                type="button"
                className="print-size-canvas-block__apply"
                onClick={handleApplyCustomCrop}
              >
                Apply
              </button>
            )}
          </div>
        )}

        <div className="print-option-row">
          <label className="form-label">Gallery Wrap</label>

          <select
            className="form-select"
            value={sideType}
            onChange={(e) => setSideType(e.target.value)}
          >
            {galleryOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="print-description">
          A gallery image wrap on canvas is a technique where the printed or painted artwork stretches entirely over the edges of a thick wooden frame and is stapled to the back.
        </div>

        <div className="print-option-row frame-row">
          <label className="form-label">Frame</label>

          <div className="frame-dropdown-display" onClick={() => setFrameOpen(true)}>
            <span>
              {selectedFrame ? selectedFrame.name : 'Select Frame'}
            </span>
            <span className="frame-arrow">&rsaquo;</span>
          </div>
        </div>

        <div className="print-description float-frame-gap-disclaimer">
          <span>{FLOAT_FRAME_GAP_DISCLAIMER_PREFIX}</span>

          <div className="float-frame-gap-disclaimer__control">
            <input
              id="float-frame-gap-mm"
              type="number"
              className="print-size-canvas-block__input"
              min={0}
              step={0.5}
              inputMode="decimal"
              value={floatFrameGapMm}
              onChange={(e) => setFloatFrameGapMm(e.target.value)}
            />

            <span>mm</span>
          </div>
        </div>

        <div className="price-cart-row">
          <div className="product-price">
            ${totalPrice.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)} = ${totalWithGST.toFixed(2)}
          </div>

          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? `${progress}%` : 'Add to Cart'}
          </button>
        </div>
        <FrameStickSpecPanel frame={selectedFrame} />
      </div>

      {frameOpen && (
        <div className="frame-sidebar open" onClick={(e) => e.stopPropagation()}>
          <div className="frame-sidebar-header">
            <h3>Frames</h3>
            <button onClick={() => setFrameOpen(false)}>x</button>
          </div>

          {FrameList.map((frame) => (
            <div
              key={frame.id}
              className={`frame-item ${selectedFrame?.id === frame.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedFrame(frame);
                setFrameOpen(false);
              }}
            >
              <img src={frame.img} alt={frame.name} />
              <div className="frame-meta">
                <div className="frame-name">{frame.name}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default CanvasPrintAndFraming;
